import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useStore } from 'store'
import PatientContext from 'contexts/PatientContext'
import { SharedCareQuestionnaireItem } from './constants'
import useOnValueChange from '../hooks/useOnValueChange'
import { getAdlNeeds } from '../components/ADLNeeds/getAdlNeeds'

interface CertPeriodGoal {
  id: number
  category: string
  text: string
}

interface UseNarrativeProps {
  hasHospitalized?: string
  hasVisitedER?: string
  hasInfection?: string
  hasInjuries?: string
  hasFalls?: string
  hasVisitedMD?: string
  hospitalNotes?: string
  erNotes?: string
  infectionNotes?: string
  injuryNotes?: string
  fallNotes?: string
  mdNotes?: string
  additionalServices?: string[]
  waivers?: string[]
  neuroSystemSummary?: string
  cardiacSystemSummary?: string
  respiratorySystemSummary?: string
  cognitiveSystemSummary?: string
  musculoskeletalSystemSummary?: string
  linesDrainsSystemSummary?: string
  sensorySystemSummary?: string
  painSystemSummary?: string
  endocrineSystemSummary?: string
  giSystemSummary?: string
  integumentarySystemSummary?: string
  adlNeeds?: string
  backgroundString?: string
  certPeriodGoals?: CertPeriodGoal[]
  careCoordinationPreviousAppointments?: string
  careCoordinationFutureAppointments?: string
  careCoordinationCareTeamCoordination?: string
  careCoordinationOrdersForTreatment?: string
}

export const AUTO_GENERATED_NARRATIVE_KEY = '=== auto-generated ==='
export const ORDERS_FOR_TREATMENT_HEADER = 'Orders for Treatment:'

export enum AutoGeneratedNarrativeSteps {
  INITIAL = 'initial',
  AUTO_GENERATED = 'auto-generated',
  EDITED = 'edited'
}

export const NoEditedAutoGeneratedNarrativeSteps = [
  AutoGeneratedNarrativeSteps.INITIAL,
  AutoGeneratedNarrativeSteps.AUTO_GENERATED
]

const useNarrative = ({
  hasHospitalized,
  hasVisitedER,
  hasInfection,
  hasInjuries,
  hasFalls,
  hasVisitedMD,
  hospitalNotes,
  erNotes,
  infectionNotes,
  injuryNotes,
  fallNotes,
  mdNotes,
  additionalServices,
  waivers,
  neuroSystemSummary,
  cardiacSystemSummary,
  respiratorySystemSummary,
  cognitiveSystemSummary,
  musculoskeletalSystemSummary,
  linesDrainsSystemSummary,
  sensorySystemSummary,
  painSystemSummary,
  endocrineSystemSummary,
  giSystemSummary,
  integumentarySystemSummary,
  adlNeeds,
  backgroundString,
  certPeriodGoals,
  careCoordinationPreviousAppointments,
  careCoordinationFutureAppointments,
  careCoordinationCareTeamCoordination,
  careCoordinationOrdersForTreatment
}: UseNarrativeProps): {
  systemSummariesStep: AutoGeneratedNarrativeSteps
  setSystemSummariesStep: React.Dispatch<React.SetStateAction<AutoGeneratedNarrativeSteps>>
  patientGoalsStep: AutoGeneratedNarrativeSteps
  setPatientGoalsStep: React.Dispatch<React.SetStateAction<AutoGeneratedNarrativeSteps>>
  careCoordinationStep: AutoGeneratedNarrativeSteps
  setCareCoordinationStep: React.Dispatch<React.SetStateAction<AutoGeneratedNarrativeSteps>>
} => {
  const { patientId } = useContext(PatientContext)
  const { flatAnswers, cantEditAssessmentFields, assessmentStatusState } = useAssessmentContext()
  const { patSunset } = useFeatureFlags()
  const { idToPatientMapping } = useStore((state) => state.patient)
  const patientData = patientId ? idToPatientMapping[patientId] : undefined
  const serviceTypes = patientData?.serviceTypes
  const onValueChange = useOnValueChange()
  const BACKGROUND_TEXT = backgroundString
  const ADL_NEEDS_TEXT = adlNeeds

  const [systemSummariesStep, setSystemSummariesStep] = useState<AutoGeneratedNarrativeSteps>(
    AutoGeneratedNarrativeSteps.INITIAL
  )
  const [initialSystemSummaries, setInitialSystemSummaries] = useState<string | undefined>(
    undefined
  )

  const [patientGoalsStep, setPatientGoalsStep] = useState<AutoGeneratedNarrativeSteps>(
    AutoGeneratedNarrativeSteps.INITIAL
  )
  const [initialPatientGoals, setInitialPatientGoals] = useState<string | undefined>(undefined)

  const [careCoordinationStep, setCareCoordinationStep] = useState<AutoGeneratedNarrativeSteps>(
    AutoGeneratedNarrativeSteps.INITIAL
  )
  const [initialCareCoordination, setInitialCareCoordination] = useState<string | undefined>(
    undefined
  )

  const formattedAdditionalServices = additionalServices?.join(', ')
  const formattedWaivers = waivers?.join(', ')

  const SYSTEM_SUMMARIES_TEXT = `
  ${AUTO_GENERATED_NARRATIVE_KEY}\n
  ${[
    `Neurological System: ${neuroSystemSummary ?? ''}`,
    `Cardiac System: ${cardiacSystemSummary ?? ''}`,
    `Respiratory System: ${respiratorySystemSummary ?? ''}`,
    `Cognitive and Psychosocial Development: ${cognitiveSystemSummary ?? ''}`,
    `Musculoskeletal System: ${musculoskeletalSystemSummary ?? ''}`,
    `Lines/Drains System: ${linesDrainsSystemSummary ?? ''}`,
    `Sensory System: ${sensorySystemSummary ?? ''}`,
    `Pain System: ${painSystemSummary ?? ''}`,
    `Endocrine System: ${endocrineSystemSummary ?? ''}`,
    `Gastrointestinal System: ${giSystemSummary ?? ''}`,
    `Integumentary System: ${integumentarySystemSummary ?? ''}`
  ].join('\n\n')}`

  let CLINICAL_TEXT: string | undefined
  if (hasHospitalized && hasVisitedER && hasInfection && hasInjuries && hasFalls && hasVisitedMD) {
    CLINICAL_TEXT = [
      `Hospitalization visits in the last 60 days: ${hasHospitalized}${
        hospitalNotes ? `\n${hospitalNotes}\n` : ''
      }`,
      `ER visits in the last 60 days: ${hasVisitedER}${erNotes ? `\n${erNotes}\n` : ''}`,
      `Infections in the last 60 days: ${hasInfection}${
        infectionNotes ? `\n${infectionNotes}\n` : ''
      }`,
      `Injuries in the last 60 days: ${hasInjuries}${injuryNotes ? `\n${injuryNotes}\n` : ''}`,
      `Falls in the last 60 days: ${hasFalls}${fallNotes ? `\n${fallNotes}\n` : ''}`,
      `MD visits in the last 60 days: ${hasVisitedMD}${mdNotes ? `\n${mdNotes}\n` : ''}`
    ].join('\n')
  }

  const PATIENT_GOALS_TEMPLATE = `
  ${AUTO_GENERATED_NARRATIVE_KEY}
  \n
  Certification period goals:
  ${certPeriodGoals?.map((goal) => `* ${goal.category}: ${goal.text}`).join('\n') ?? ''}
  `

  const CARE_COORDINATION_TEMPLATE = `
  ${AUTO_GENERATED_NARRATIVE_KEY}\n
  Previous Appointments:
  ${careCoordinationPreviousAppointments ?? ''}\n
  Future Appointments:
  ${careCoordinationFutureAppointments ?? ''}\n
  Care Team Coordination:
  ${careCoordinationCareTeamCoordination ?? ' '}\n
  ${ORDERS_FOR_TREATMENT_HEADER}
  ${careCoordinationOrdersForTreatment ?? ''}
  `

  const PATIENT_SERVICES_TEMPLATE = `Additional Services: ${
    formattedAdditionalServices ?? ''
  }\nWaivers: ${formattedWaivers ?? ''}`

  const prevSystemSummaries = useMemo(
    () =>
      flatAnswers[SharedCareQuestionnaireItem('NARRATIVE_SYSTEM_SUMMARIES')]?.answer?.[0]
        .valueString,
    [flatAnswers]
  )

  const prevPatientGoals = useMemo(
    () =>
      flatAnswers[SharedCareQuestionnaireItem('NARRATIVE_PATIENT_GOALS')]?.answer?.[0].valueString,
    [flatAnswers]
  )

  const prevCareCoordination = useMemo(
    () =>
      flatAnswers[SharedCareQuestionnaireItem('NARRATIVE_CARE_COORDINATION')]?.answer?.[0]
        .valueString,
    [flatAnswers]
  )

  useEffect(() => {
    if (!initialSystemSummaries) {
      if (!prevSystemSummaries && !cantEditAssessmentFields) {
        onValueChange({
          linkId: SharedCareQuestionnaireItem('NARRATIVE_SYSTEM_SUMMARIES'),
          value: SYSTEM_SUMMARIES_TEXT
        })
        setInitialSystemSummaries(SYSTEM_SUMMARIES_TEXT)
        setSystemSummariesStep(AutoGeneratedNarrativeSteps.AUTO_GENERATED)
      } else {
        setInitialSystemSummaries(prevSystemSummaries)
        if (
          prevSystemSummaries !== SYSTEM_SUMMARIES_TEXT &&
          prevSystemSummaries?.includes(AUTO_GENERATED_NARRATIVE_KEY) === false
        ) {
          setSystemSummariesStep(AutoGeneratedNarrativeSteps.EDITED)
        } else {
          setSystemSummariesStep(AutoGeneratedNarrativeSteps.AUTO_GENERATED)
        }
      }
    }
  }, [
    prevSystemSummaries,
    initialSystemSummaries,
    SYSTEM_SUMMARIES_TEXT,
    onValueChange,
    cantEditAssessmentFields
  ])

  useEffect(() => {
    if (!initialPatientGoals) {
      if (!prevPatientGoals && !cantEditAssessmentFields) {
        onValueChange({
          linkId: SharedCareQuestionnaireItem('NARRATIVE_PATIENT_GOALS'),
          value: PATIENT_GOALS_TEMPLATE
        })
        setInitialPatientGoals(PATIENT_GOALS_TEMPLATE)
        setPatientGoalsStep(AutoGeneratedNarrativeSteps.AUTO_GENERATED)
      } else {
        if (
          prevPatientGoals !== PATIENT_GOALS_TEMPLATE &&
          prevPatientGoals?.includes(AUTO_GENERATED_NARRATIVE_KEY) === false
        ) {
          setPatientGoalsStep(AutoGeneratedNarrativeSteps.EDITED)
        } else {
          setPatientGoalsStep(AutoGeneratedNarrativeSteps.AUTO_GENERATED)
        }
        setInitialPatientGoals(prevPatientGoals)
      }
    }
  }, [
    prevPatientGoals,
    initialPatientGoals,
    PATIENT_GOALS_TEMPLATE,
    onValueChange,
    cantEditAssessmentFields
  ])

  useEffect(() => {
    if (!initialCareCoordination) {
      if (!prevCareCoordination && !cantEditAssessmentFields) {
        onValueChange({
          linkId: SharedCareQuestionnaireItem('NARRATIVE_CARE_COORDINATION'),
          value: CARE_COORDINATION_TEMPLATE
        })
        setInitialCareCoordination(CARE_COORDINATION_TEMPLATE)
        setCareCoordinationStep(AutoGeneratedNarrativeSteps.AUTO_GENERATED)
      } else {
        if (
          prevCareCoordination !== CARE_COORDINATION_TEMPLATE &&
          prevCareCoordination?.includes(AUTO_GENERATED_NARRATIVE_KEY) === false
        ) {
          setCareCoordinationStep(AutoGeneratedNarrativeSteps.EDITED)
        } else {
          setCareCoordinationStep(AutoGeneratedNarrativeSteps.AUTO_GENERATED)
        }
        setInitialCareCoordination(prevCareCoordination)
      }
    }
  }, [
    prevCareCoordination,
    initialCareCoordination,
    CARE_COORDINATION_TEMPLATE,
    onValueChange,
    cantEditAssessmentFields
  ])

  useEffect(() => {
    if (
      SYSTEM_SUMMARIES_TEXT !== prevSystemSummaries &&
      systemSummariesStep === AutoGeneratedNarrativeSteps.AUTO_GENERATED &&
      !cantEditAssessmentFields
    ) {
      // If the user hasn't edited the system summaries, update the value
      onValueChange({
        linkId: SharedCareQuestionnaireItem('NARRATIVE_SYSTEM_SUMMARIES'),
        value: SYSTEM_SUMMARIES_TEXT
      })
    }
  }, [
    prevSystemSummaries,
    onValueChange,
    SYSTEM_SUMMARIES_TEXT,
    systemSummariesStep,
    initialSystemSummaries,
    cantEditAssessmentFields
  ])

  useEffect(() => {
    if (
      PATIENT_GOALS_TEMPLATE !== prevPatientGoals &&
      patientGoalsStep === AutoGeneratedNarrativeSteps.AUTO_GENERATED &&
      !cantEditAssessmentFields
    ) {
      // If the user hasn't edited the patient goals summaries, update the value
      onValueChange({
        linkId: SharedCareQuestionnaireItem('NARRATIVE_PATIENT_GOALS'),
        value: PATIENT_GOALS_TEMPLATE
      })
    }
  }, [
    prevPatientGoals,
    onValueChange,
    PATIENT_GOALS_TEMPLATE,
    patientGoalsStep,
    initialPatientGoals,
    cantEditAssessmentFields
  ])

  useEffect(() => {
    if (
      CARE_COORDINATION_TEMPLATE !== prevCareCoordination &&
      careCoordinationStep === AutoGeneratedNarrativeSteps.AUTO_GENERATED &&
      !cantEditAssessmentFields
    ) {
      // If the user hasn't edited the patient goals summaries, update the value
      onValueChange({
        linkId: SharedCareQuestionnaireItem('NARRATIVE_CARE_COORDINATION'),
        value: CARE_COORDINATION_TEMPLATE
      })
    }
  }, [
    prevCareCoordination,
    onValueChange,
    CARE_COORDINATION_TEMPLATE,
    careCoordinationStep,
    initialCareCoordination,
    cantEditAssessmentFields
  ])

  useEffect(() => {
    const { shouldRenderADLNeeds: isAdlNeedsAllowed } = getAdlNeeds({
      patSunset,
      flatAnswers,
      serviceTypes: serviceTypes ?? undefined,
      assessmentStatus: assessmentStatusState.status ?? undefined
    })

    const prevBackground =
      flatAnswers[SharedCareQuestionnaireItem('NARRATIVE_BACKGROUND')]?.answer?.[0]?.valueString
    const prevClinical =
      flatAnswers[SharedCareQuestionnaireItem('NARRATIVE_CLINICAL_OVERVIEW')]?.answer?.[0]
        ?.valueString
    const prevAdlNeeds =
      flatAnswers[SharedCareQuestionnaireItem('NARRATIVE_ADL_NEEDS')]?.answer?.[0]?.valueString
    const prevPatientServices =
      flatAnswers[SharedCareQuestionnaireItem('NARRATIVE_PATIENT_SERVICES')]?.answer?.[0]
        ?.valueString

    if (!cantEditAssessmentFields) {
      if (BACKGROUND_TEXT && BACKGROUND_TEXT !== prevBackground) {
        onValueChange({
          linkId: SharedCareQuestionnaireItem('NARRATIVE_BACKGROUND'),
          value: BACKGROUND_TEXT
        })
      }
      if (CLINICAL_TEXT && CLINICAL_TEXT !== prevClinical) {
        onValueChange({
          linkId: SharedCareQuestionnaireItem('NARRATIVE_CLINICAL_OVERVIEW'),
          value: CLINICAL_TEXT
        })
      }
      // If PAT sunset is enabled, we should only update the ADL needs
      if (isAdlNeedsAllowed && ADL_NEEDS_TEXT && ADL_NEEDS_TEXT !== prevAdlNeeds) {
        onValueChange({
          linkId: SharedCareQuestionnaireItem('NARRATIVE_ADL_NEEDS'),
          value: ADL_NEEDS_TEXT
        })
      }
      if (PATIENT_SERVICES_TEMPLATE && PATIENT_SERVICES_TEMPLATE !== prevPatientServices) {
        onValueChange({
          linkId: SharedCareQuestionnaireItem('NARRATIVE_PATIENT_SERVICES'),
          value: PATIENT_SERVICES_TEMPLATE
        })
      }
    }
  }, [
    BACKGROUND_TEXT,
    CLINICAL_TEXT,
    ADL_NEEDS_TEXT,
    PATIENT_SERVICES_TEMPLATE,
    onValueChange,
    flatAnswers,
    patSunset,
    cantEditAssessmentFields,
    serviceTypes,
    assessmentStatusState.status
  ])

  return {
    systemSummariesStep,
    setSystemSummariesStep,
    patientGoalsStep,
    setPatientGoalsStep,
    careCoordinationStep,
    setCareCoordinationStep
  }
}

export default useNarrative
